import { Col, Modal, Row } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import hitches, { Hitches } from '../../services/apps/hitches';
import type { Hitch } from '../../services/apps/hitches';
import HitchDetailSummary from '../../components/HitchDetailSummary';
import {
  GetStaticPathsContext,
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next';
import withPublicWrapper from 'components/public/Wrapper/withPublicWrapper';
import SubscribeForm from 'components/SubscribeForm';
import { createIntro, getInterestState } from 'components/SubscribeForm/utils';
import IntroBlock from 'components/public/Intro/IntroBlock';
import HitchNextSteps from 'components/HitchById/HitchNextSteps';
import useUpdateUrl, {
  UpdateUrlQueryParams,
} from 'components/HitchById/HitchNextSteps/utils/useUpdateUrl';
import Head from 'next/head';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import styles from './styles.module.css';

export type HitchDetailProps = {
  mainHitch?: Hitch;
  secondaryHitch?: Hitch;
};

const hitchesColResponsiveProps = {
  xs: 24,
  sm: 12,
  md: 10,
  lg: 8,
  xl: 8,
};

const HitchDetail = ({
  mainHitch: mainHitchProp,
  secondaryHitch: secondaryHitchProp,
}: HitchDetailProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const { query } = useRouter();

  // formatMessage(
  //   {
  //     defaultMessage: `Don't worry, as we already integrate with {app}, it could be a peace of cake to support your other integration as well.`,
  //     description: 'integrations/one-not-supported/description/1',
  //   },
  //   { app: 'interestState.supportedAppName' }
  // );
  const selectAppsRowRef = useRef<HTMLDivElement>(null);

  const [{ mainHitch, secondaryHitch }, setHitches] = useState<HitchDetailProps>({
    mainHitch: mainHitchProp,
    secondaryHitch: secondaryHitchProp,
  });

  const interestState = getInterestState(mainHitch, secondaryHitch);
  const { introTitle, introDescription, introVideo, demoVideo } = createIntro(
    interestState,
    formatMessage
  );
  const [openModal, setOpenModal] = useState<boolean>(false);

  const subscribeForm = (
    <IntroBlock
      type="green-top"
      title={introTitle}
      description={
        <>
          <Col span={24}>{introDescription}</Col>
          <Col span={24}>
            <SubscribeForm firstApp={interestState.firstApp} secondApp={interestState.secondApp} />
          </Col>
        </>
      }
    />
  );

  const { updateUrl } = useUpdateUrl();

  useEffect(() => {
    if (
      !query.id ||
      query.id.length < 2 ||
      query.id[0] !== mainHitch?.id ||
      query.id[1] !== secondaryHitch?.id
    ) {
      updateUrl({ id: [mainHitch?.id || 'other', secondaryHitch?.id || 'other'] });
      selectAppsRowRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      switch (interestState.state) {
        case 'two-app-one-supported':
        case 'two-app-not-supported':
          setOpenModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainHitch, secondaryHitch, interestState.state]);

  const title =
    !mainHitch?.name && !secondaryHitch?.name
      ? 'Select apps'
      : `${mainHitch?.name || secondaryHitch?.name} - ${
          mainHitch?.name ? secondaryHitch?.name || 'Select one' : 'Select one'
        }`;

  const descriptionText =
    !mainHitch?.name && !secondaryHitch?.name
      ? 'Connect apps'
      : `Connect ${mainHitch?.name || secondaryHitch?.name} with ${
          mainHitch?.name ? secondaryHitch?.name || 'another one' : 'another one'
        }`;

  const app1 = (
    <HitchDetailSummary
      deletable
      hitch={mainHitch}
      otherHitch={secondaryHitch}
      onSelect={(hitchId?: string) => {
        setHitches({ mainHitch: hitchId ? Hitches[hitchId] : undefined, secondaryHitch });
      }}
    />
  );
  const app2 = (
    <HitchDetailSummary
      deletable
      hitch={secondaryHitch}
      otherHitch={mainHitch}
      onSelect={(hitchId?: string) => {
        setHitches({ mainHitch, secondaryHitch: hitchId ? Hitches[hitchId] : undefined });
      }}
    />
  );

  return (
    <div>
      <Head>
        <title>{title}</title>
      </Head>
      <IntroBlock
        className={styles.apps}
        type="white"
        description={
          (
            <>
              <Col {...hitchesColResponsiveProps}>{app1}</Col>
              <Col {...hitchesColResponsiveProps}>{app2}</Col>
            </>
          ) || (
            <div>
              <p>{descriptionText}</p>
            </div>
          )
        }
      />
      {introVideo}

      {demoVideo}
      <HitchNextSteps interestState={interestState} />
      {(!mainHitch || !secondaryHitch) && (
        <IntroBlock
          type="white"
          subTitle={formatMessage({
            defaultMessage: 'Choose a second app',
            description: 'integrations/one-not-supported/another/title',
          })}
          className={styles.chooseApp}
          description={
            <Row justify="center">
              <Col span={24}>
                <p>
                  {formatMessage({
                    defaultMessage: 'And find out what is possible with this combination.',
                    description: 'integrations/one-not-supported/another/description',
                  })}{' '}
                </p>
              </Col>
              <Col>{mainHitch ? app2 : app1}</Col>
            </Row>
          }
        />
      )}
      {subscribeForm}
      <Modal
        visible={openModal}
        onOk={() => setOpenModal(false)}
        onCancel={() => setOpenModal(false)}
        title={introTitle}
      >
        <>
          <Col span={24}>{introDescription}</Col>
          <Col span={24}>
            <SubscribeForm
              firstApp={interestState.firstApp}
              secondApp={interestState.secondApp}
              onSubmit={() => setOpenModal(false)}
            />
          </Col>
        </>
      </Modal>
    </div>
  );
};

export async function getStaticPaths({
  locales,
}: GetStaticPathsContext): Promise<GetStaticPathsResult> {
  const hitchesWithOther = [...hitches, { id: 'other' }];
  const integrationPaths = hitchesWithOther.map(({ id }) => {
    return hitchesWithOther.map(({ id: secondaryHitchId }) => {
      const route = {
        params: { id: [id, secondaryHitchId] },
      };
      return [
        route,
        ...(locales
          ? locales.map((locale) => ({
              ...route,
              locale,
            }))
          : []),
      ];
    });
  });

  type RoutePaths = {
    params: {
      id: string[];
    };
    locale?: string;
  };

  return {
    paths: ([] as RoutePaths[]).concat(...([] as RoutePaths[][]).concat(...integrationPaths)),
    fallback: false,
  };
}

export async function getStaticProps(
  context: GetStaticPropsContext<UpdateUrlQueryParams>
): Promise<GetStaticPropsResult<HitchDetailProps>> {
  const [mainHitchId, secondaryHitchId] = context?.params?.id || [];

  return {
    props: {
      ...(mainHitchId !== 'other' ? { mainHitch: Hitches[mainHitchId] } : {}),
      ...(secondaryHitchId !== 'other' ? { secondaryHitch: Hitches[secondaryHitchId] } : {}),
    },
  };
}

export default withPublicWrapper(HitchDetail, { withSider: false, withGoogleOneTap: true });
